import { useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchNotifications } from "../../features/notification/notificationActions";

/**
 * Custom hook for infinite scroll functionality
 * Automatically loads more notifications when user scrolls near bottom
 */
export const useInfiniteScroll = ({
  threshold = 200, // Distance from bottom to trigger load
  enabled = true,
  containerRef = null,
}) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { pagination, loading, filters } = useSelector(
    (state) => state.notifications
  );

  const loadingRef = useRef(false);
  const observerRef = useRef(null);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (
      !enabled ||
      loadingRef.current ||
      loading.notifications ||
      !pagination.hasNext
    ) {
      return;
    }

    loadingRef.current = true;

    try {
      const token = await getAccessTokenSilently();
      await dispatch(
        fetchNotifications(
          {
            page: pagination.page + 1,
            limit: pagination.limit || 20,
            ...filters,
          },
          true, // append to existing notifications
          true, // use cache
          token
        )
      );
    } catch (error) {
      console.error("Failed to load more notifications:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [
    dispatch,
    getAccessTokenSilently,
    enabled,
    loading.notifications,
    pagination.hasNext,
    pagination.page,
    pagination.limit,
    filters,
  ]);

  // Scroll event handler
  const handleScroll = useCallback(
    (event) => {
      if (!enabled || loadingRef.current || !pagination.hasNext) {
        return;
      }

      const target = event.target;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;

      // Check if user has scrolled near the bottom
      if (scrollHeight - scrollTop - clientHeight < threshold) {
        loadMore();
      }
    },
    [enabled, loadMore, pagination.hasNext, threshold]
  );

  // Intersection Observer for better performance
  const setupIntersectionObserver = useCallback(() => {
    if (!enabled || !containerRef?.current) {
      return;
    }

    // Create a sentinel element at the bottom
    const sentinel = document.createElement("div");
    sentinel.style.height = "1px";
    sentinel.style.position = "absolute";
    sentinel.style.bottom = `${threshold}px`;
    sentinel.style.width = "100%";
    sentinel.setAttribute("data-infinite-scroll-sentinel", "true");

    containerRef.current.appendChild(sentinel);

    // Set up intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            pagination.hasNext &&
            !loadingRef.current
          ) {
            loadMore();
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: `0px 0px ${threshold}px 0px`,
        threshold: 0,
      }
    );

    observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (sentinel.parentNode) {
        sentinel.parentNode.removeChild(sentinel);
      }
    };
  }, [enabled, containerRef, threshold, pagination.hasNext, loadMore]);

  // Set up scroll listener or intersection observer
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef?.current;

    if (container) {
      // Use intersection observer if container ref is provided
      return setupIntersectionObserver();
    } else {
      // Fall back to window scroll listener
      const handleWindowScroll = () => {
        if (!enabled || loadingRef.current || !pagination.hasNext) {
          return;
        }

        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;

        if (scrollHeight - scrollTop - clientHeight < threshold) {
          loadMore();
        }
      };

      window.addEventListener("scroll", handleWindowScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleWindowScroll);
    }
  }, [
    enabled,
    containerRef,
    setupIntersectionObserver,
    pagination.hasNext,
    threshold,
    loadMore,
  ]);

  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    loadMore,
    isLoading: loading.notifications,
    hasMore: pagination.hasNext,
    handleScroll: containerRef ? undefined : handleScroll, // Only provide scroll handler if no container ref
  };
};

export default useInfiniteScroll;
